import React from "react";
import { FiChevronRight } from "react-icons/fi";
import { IconTile, Skeleton, Widget, WidgetGrid } from "../../activity-logs/styles";
import { colorFor } from "../../activity-logs/labels";
import { HeaderLink, StorageBar, TopHeadline, TopRow, Tag } from "../styles";
import { SupplierAvatar } from "./parts";
import { categoryMeta, currencyParts, displayName, formatCount, formatPercent, taxMeta } from "../dashboardData";

const TOP = 5;

const itemsLabel = (n) => `${formatCount(n)} ${n === 1 ? "item" : "itens"}`;

/**
 * Cartão de destaque, no padrão da tela de armazenamento do iOS: quanto os
 * primeiros concentram, uma barra com a parte de cada um e a lista logo abaixo.
 * Cada segmento da barra tem a cor do ícone da linha correspondente.
 */
function TopCard({ title, sub, linkLabel, onLink, share, shareLabel, segments, loading, message, delay, children }) {
  return (
    <Widget $delay={delay}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">{title}</div>
          {sub && <div className="w-sub">{sub}</div>}
        </div>
        <HeaderLink type="button" onClick={onLink}>{linkLabel} <FiChevronRight /></HeaderLink>
      </div>

      {loading && <div style={{ marginTop: 16 }}><Skeleton $h={220} $r={12} /></div>}
      {!loading && message && <div className="w-sub" style={{ marginTop: 16 }}>{message}</div>}
      {!loading && !message && (
        <>
          <TopHeadline>
            <span className="n">{formatPercent(share).replace("%", "")}<span className="pct">%</span></span>
            <span className="l">{shareLabel}</span>
          </TopHeadline>
          <StorageBar role="img" aria-label={`${formatPercent(share)} ${shareLabel}`}>
            {segments.map((s) => <span key={s.key} title={s.label} style={{ flexGrow: Math.max(s.share, 0.5), background: s.color }} />)}
            {share < 100 && <span className="rest" style={{ flexGrow: 100 - share }} />}
          </StorageBar>
          <div style={{ marginTop: 8 }}>{children}</div>
        </>
      )}
    </Widget>
  );
}

export function ListRow({ lead, title, fullTitle, subtitle, value, share, compact, onClick }) {
  const { currency, integer, decimal } = currencyParts(value);
  return (
    <TopRow type="button" $compact={compact} onClick={onClick}>
      <span className="lead">{lead}</span>
      <span className="main">
        <span className="title" title={fullTitle || title}>{title}</span>
        <span className="sub">{subtitle}</span>
      </span>
      <span className="end">
        <span className="value"><span className="cur">{currency}</span>{integer}<span className="dec">{decimal}</span></span>
        <span className="share">{formatPercent(share)}</span>
      </span>
      <FiChevronRight className="chevron" />
    </TopRow>
  );
}

export default function TopLists({
  suppliers, topCreditos, total, loading, periodFiltered, onGoSuppliers, onGoCredits, onOpenSupplier, onOpenCredit,
}) {
  const list = suppliers.suppliers;
  const supplierTotal = list.reduce((sum, s) => sum + s.total, 0);
  const topSuppliers = list.slice(0, TOP).map((s) => ({
    ...s, share: supplierTotal > 0 ? (s.total / supplierTotal) * 100 : 0,
  }));
  const topCredits = topCreditos.slice(0, TOP).map((c) => ({
    ...c, share: total > 0 ? (c.valor / total) * 100 : 0,
  }));
  const sumShares = (rows) => Math.min(100, rows.reduce((sum, r) => sum + r.share, 0));

  let supplierMessage = null;
  if (list.length === 0 && !suppliers.loading) {
    supplierMessage = suppliers.failed ? "Não foi possível carregar os fornecedores" : "Nenhum fornecedor identificado";
  }

  return (
    <WidgetGrid $cols={2}>
      <TopCard
        title="Principais fornecedores"
        sub={periodFiltered ? "Todo o projeto, sem o filtro de período" : undefined}
        linkLabel="Ver todos"
        onLink={onGoSuppliers}
        share={sumShares(topSuppliers)}
        shareLabel={`do crédito vem de ${topSuppliers.length === 1 ? "1 fornecedor" : `${topSuppliers.length} fornecedores`}`}
        segments={topSuppliers.map((s) => ({ key: s.id, share: s.share, color: colorFor(s.name), label: `${s.name}: ${formatPercent(s.share)}` }))}
        loading={loading || (suppliers.loading && list.length === 0)}
        message={supplierMessage}
        delay={350}
      >
        {topSuppliers.map((s) => (
          <ListRow
            key={s.id}
            lead={<SupplierAvatar name={s.name} size={32} />}
            title={displayName(s.name)}
            fullTitle={s.name}
            subtitle={[itemsLabel(s.count), s.uf].filter(Boolean).join(" · ")}
            value={s.total}
            share={s.share}
            onClick={() => onOpenSupplier(s)}
          />
        ))}
      </TopCard>

      <TopCard
        title="Maiores oportunidades"
        linkLabel="Ver todas"
        onLink={() => onGoCredits()}
        share={sumShares(topCredits)}
        shareLabel={`do crédito está em ${topCredits.length === 1 ? "1 oportunidade" : `${topCredits.length} oportunidades`}`}
        segments={topCredits.map((c) => ({ key: c.id, share: c.share, color: categoryMeta(c.categoria).color, label: `${c.titulo}: ${formatPercent(c.share)}` }))}
        loading={loading}
        delay={400}
      >
        {topCredits.map((c) => {
          const meta = categoryMeta(c.categoria);
          const tax = taxMeta(c.tipo);
          const Icon = meta.icon;
          return (
            <ListRow
              key={c.id}
              lead={<IconTile $size={32} $color={meta.color}><Icon /></IconTile>}
              title={c.titulo}
              subtitle={<><Tag $color={tax.color} style={{ fontSize: "1em" }}>{tax.label}</Tag> · {itemsLabel(c.quantidade)}</>}
              value={c.valor}
              share={c.share}
              onClick={() => onOpenCredit(c)}
            />
          );
        })}
      </TopCard>
    </WidgetGrid>
  );
}
